import React from "react";
import LegalShell, { Section } from "../components/LegalShell";

const EFFECTIVE = "7 June 2026";

export default function Terms() {
  return (
    <LegalShell title="Terms of Service" kicker="The rules of the studio" effective={EFFECTIVE}>
      <p>
        These Terms govern your use of TrainMate. By signing up you agree to them. If you use TrainMate on
        behalf of a business, "you" means you personally and that business, and you warrant you have the
        authority to bind it.
      </p>

      <Section heading="1. What TrainMate is">
        <p>TrainMate is a business-management tool for Australian personal trainers: client CRM, session
          scheduling, GST-compliant invoicing, session-pack tracking, and related workflows. Features may
          evolve over time.</p>
      </Section>

      <Section heading="2. Your account">
        <p>You sign in with a Google account. You are responsible for keeping that Google account secure.
          You must be at least 18 years old and legally able to enter into contracts under Australian law.</p>
      </Section>

      <Section heading="3. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1 text-[#2A2623]">
          <li>Upload client information without their consent, where consent is legally required</li>
          <li>Use TrainMate to send unsolicited marketing or spam</li>
          <li>Attempt to gain unauthorised access to the platform, other accounts, or underlying infrastructure</li>
          <li>Reverse-engineer, scrape, or interfere with the service</li>
          <li>Use TrainMate to store data unrelated to a bona fide personal-training business</li>
        </ul>
      </Section>

      <Section heading="4. Fees & billing">
        <p>Where TrainMate is offered on a paid subscription, fees are billed in AUD (inclusive of GST) via
          the payment method you nominate. Failed payments may result in temporary account suspension after
          7 days' notice. Refunds are handled in line with the Australian Consumer Law. You may request a
          refund if the service fails to meet a Consumer Guarantee.</p>
      </Section>

      <Section heading="5. Invoices you generate">
        <p>TrainMate produces GST-compliant invoice PDFs using the ABN, business name, and rates you enter
          in Settings. <strong>You are solely responsible</strong> for the accuracy of that information and
          for meeting your own tax and record-keeping obligations with the Australian Taxation Office.
          TrainMate provides tooling; TrainMate is not your accountant.</p>
      </Section>

      <Section heading="6. Your content, your ownership">
        <p>You retain all rights to the client data, notes, and other content you upload. You grant us a
          limited licence to store, back up, transmit, and display it strictly as needed to operate the
          service for you.</p>
      </Section>

      <Section heading="7. Uptime & service availability">
        <p>We aim for high availability but do not guarantee uninterrupted service. Scheduled maintenance
          will be announced in-app where possible. TrainMate is provided on an "as is" and "as available"
          basis, to the extent permitted by law.</p>
      </Section>

      <Section heading="8. Consumer guarantees & limitation of liability">
        <p>Nothing in these Terms excludes or limits any rights you have under the Australian Consumer Law
          that cannot lawfully be excluded. Subject to that:</p>
        <ul className="list-disc pl-6 space-y-1 text-[#2A2623]">
          <li>Our total aggregate liability for any claim relating to TrainMate is capped at the fees you paid us in the 12 months before the claim (or AUD $100 if the service was free during that period).</li>
          <li>We are not liable for indirect, consequential, or loss-of-profit damages.</li>
        </ul>
      </Section>

      <Section heading="9. Suspension & termination">
        <p>You can close your account at any time via the <a href="/data-deletion" className="text-[#0F6B6B] hover:underline">Data Deletion</a> flow.
          We may suspend or terminate accounts that breach these Terms, are used unlawfully, or remain
          inactive for more than 24 months, with reasonable notice where possible.</p>
      </Section>

      <Section heading="10. Governing law">
        <p>These Terms are governed by the laws of New South Wales, Australia. Any dispute will be resolved
          in the courts of New South Wales, without prejudice to any mandatory consumer rights available in
          your local jurisdiction within Australia.</p>
      </Section>

      <Section heading="11. Changes">
        <p>We may update these Terms; the effective date at the top will change and we will notify you
          in-app of material changes at least 14 days before they take effect. Continued use after that
          means you accept the update.</p>
      </Section>

      <Section heading="12. Contact">
        <p>Questions about these Terms: <a href="mailto:hello@trainmate.au" className="text-[#0F6B6B] hover:underline">hello@trainmate.au</a>.</p>
      </Section>
    </LegalShell>
  );
}
